import {
  getAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import app from '../config/firebase';
import { continentFromCountry } from '../constants/countries';

export type AnalyticsStartupEvent = 'view_startup' | 'favorite_startup';

export type AnalyticsStartupPayload = {
  startupId: string;
  country?: string;
  industry?: string;
  source?: string;
};

let analyticsInstance: Analytics | null = null;
let analyticsInitPromise: Promise<Analytics | null> | null = null;

export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return false;
    const prefs = JSON.parse(raw) as { analytics?: boolean };
    return prefs.analytics === true;
  } catch {
    return false;
  }
}

export async function initFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === 'undefined') return null;
  if (!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) return null;

  if (analyticsInstance) {
    setAnalyticsCollectionEnabled(analyticsInstance, hasAnalyticsConsent());
    return analyticsInstance;
  }

  if (!analyticsInitPromise) {
    analyticsInitPromise = (async () => {
      const supported = await isSupported();
      if (!supported) return null;

      analyticsInstance = getAnalytics(app);
      setAnalyticsCollectionEnabled(analyticsInstance, hasAnalyticsConsent());
      return analyticsInstance;
    })();
  }

  return analyticsInitPromise;
}

export function syncAnalyticsConsent(enabled: boolean): void {
  if (!analyticsInstance) return;
  setAnalyticsCollectionEnabled(analyticsInstance, enabled);
}

export async function logAnalyticsPageView(pagePath: string): Promise<void> {
  if (!hasAnalyticsConsent()) return;

  const analytics = await initFirebaseAnalytics();
  if (!analytics) return;

  logEvent(analytics, 'page_view', { page_path: pagePath });
}

function clipParam(value: string | undefined, max = 100): string {
  return (value || '').trim().slice(0, max);
}

/**
 * Startup events for GA Explore (country / continent filters).
 * Cookie opt-in only. Never send name or email.
 */
export async function logAnalyticsStartupEvent(
  name: AnalyticsStartupEvent,
  payload: AnalyticsStartupPayload
): Promise<void> {
  if (!hasAnalyticsConsent()) return;
  const startupId = clipParam(payload.startupId);
  if (!startupId) return;

  const analytics = await initFirebaseAnalytics();
  if (!analytics) return;

  const country = clipParam(payload.country);
  logEvent(analytics, name, {
    startup_id: startupId,
    startup_country: country,
    startup_continent: continentFromCountry(country),
    startup_industry: clipParam(payload.industry),
    source: clipParam(payload.source),
  });
}
